/**
 * Topics Management Component
 *
 * CRUD for notification topics per merchant.
 * System topics (IsSystem=true) are shown but protected from deletion and code edits.
 * Custom topics can be created, edited, and deleted.
 */

import React, { useEffect, useRef, useState } from "react";
import { AppCard } from "../../components/ui";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dialog } from "primereact/dialog";
import { confirmDialog } from "primereact/confirmdialog";
import { Pencil, Trash2, Lock } from "lucide-react";
import Loader from "../../components/Loader";
import { topicsApiService } from "../../services/api/notifications";
import { NotificationTopicDto } from "../../models/notifications";

interface TopicsProps {
    merchantId: number;
}

interface TopicForm {
    topicCode: string;
    displayName: string;
    description: string;
    isActive: boolean;
}

const emptyForm: TopicForm = {
    topicCode: "",
    displayName: "",
    description: "",
    isActive: true,
};

const Topics: React.FC<TopicsProps> = ({ merchantId }) => {
    const toast = useRef<Toast>(null);

    const [isLoading, setIsLoading] = useState(true);
    const [topics, setTopics] = useState<NotificationTopicDto[]>([]);
    const [filteredTopics, setFilteredTopics] = useState<NotificationTopicDto[]>([]);
    const [globalFilter, setGlobalFilter] = useState("");
    const [showInactive, setShowInactive] = useState(false);

    // Editor state
    const [editorVisible, setEditorVisible] = useState(false);
    const [editingTopic, setEditingTopic] = useState<NotificationTopicDto | null>(null);
    const [form, setForm] = useState<TopicForm>(emptyForm);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSaving, setIsSaving] = useState(false);

    const isEditMode = editingTopic !== null;
    const isSystemTopic = editingTopic?.isSystem === true;

    useEffect(() => {
        loadTopics();
    }, [merchantId]);

    useEffect(() => {
        filterTopics();
    }, [topics, globalFilter, showInactive]);

    const loadTopics = async () => {
        setIsLoading(true);
        const result = await topicsApiService.getAll(merchantId);
        if (result.success) {
            setTopics(result.data);
        } else {
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: result.message || "Failed to load topics",
                life: 4000,
            });
        }
        setIsLoading(false);
    };

    const filterTopics = () => {
        let filtered = topics;

        if (!showInactive) {
            filtered = filtered.filter((t) => t.isActive);
        }

        if (globalFilter.trim()) {
            const filter = globalFilter.toLowerCase();
            filtered = filtered.filter(
                (t) =>
                    t.topicCode.toLowerCase().includes(filter) ||
                    t.displayName.toLowerCase().includes(filter) ||
                    t.fcmTopicName.toLowerCase().includes(filter) ||
                    t.description?.toLowerCase().includes(filter)
            );
        }

        setFilteredTopics(filtered);
    };

    const slugify = (value: string): string => {
        return value
            .toLowerCase()
            .replace(/\s+/g, "_")
            .replace(/[^a-z0-9_]/g, "");
    };

    const openCreate = () => {
        setEditingTopic(null);
        setForm(emptyForm);
        setErrors({});
        setEditorVisible(true);
    };

    const openEdit = (topic: NotificationTopicDto) => {
        setEditingTopic(topic);
        setForm({
            topicCode: topic.topicCode,
            displayName: topic.displayName,
            description: topic.description ?? "",
            isActive: topic.isActive,
        });
        setErrors({});
        setEditorVisible(true);
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!isEditMode) {
            if (!form.topicCode.trim()) {
                newErrors.topicCode = "Topic code is required";
            } else if (!/^[a-z0-9_]+$/.test(form.topicCode)) {
                newErrors.topicCode = "Topic code must contain only lowercase letters, numbers, and underscores";
            } else if (form.topicCode.length > 100) {
                newErrors.topicCode = "Topic code must be 100 characters or less";
            }
        }

        if (!form.displayName.trim()) {
            newErrors.displayName = "Display name is required";
        } else if (form.displayName.length > 200) {
            newErrors.displayName = "Display name must be 200 characters or less";
        }

        if (form.description.length > 500) {
            newErrors.description = "Description must be 500 characters or less";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;

        setIsSaving(true);

        if (isEditMode) {
            const result = await topicsApiService.update(merchantId, editingTopic!.topicId, {
                displayName: form.displayName,
                description: form.description || undefined,
                isActive: isSystemTopic ? undefined : form.isActive,
            });

            if (result.success) {
                toast.current?.show({
                    severity: "success",
                    summary: "Updated",
                    detail: `Topic "${form.displayName}" has been updated`,
                    life: 3000,
                });
                setEditorVisible(false);
                loadTopics();
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Error",
                    detail: result.message || "Failed to update topic",
                    life: 4000,
                });
            }
        } else {
            const result = await topicsApiService.create({
                merchantId,
                topicCode: form.topicCode,
                displayName: form.displayName,
                description: form.description || undefined,
                isActive: form.isActive,
            });

            if (result.success) {
                toast.current?.show({
                    severity: "success",
                    summary: "Created",
                    detail: `Topic "${form.topicCode}" has been created`,
                    life: 3000,
                });
                setEditorVisible(false);
                loadTopics();
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Error",
                    detail: result.message || "Failed to create topic",
                    life: 4000,
                });
            }
        }

        setIsSaving(false);
    };

    const handleDelete = (topic: NotificationTopicDto) => {
        if (topic.isSystem) return;

        confirmDialog({
            message: `Are you sure you want to delete the topic "${topic.displayName}"? This action cannot be undone.`,
            header: "Delete Topic",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            accept: async () => {
                const result = await topicsApiService.delete(merchantId, topic.topicId);
                if (result.success) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Deleted",
                        detail: `Topic "${topic.displayName}" has been deleted`,
                        life: 3000,
                    });
                    loadTopics();
                } else {
                    toast.current?.show({
                        severity: "error",
                        summary: "Error",
                        detail: result.message || "Failed to delete topic",
                        life: 4000,
                    });
                }
            },
        });
    };

    // Column templates
    const topicCodeTemplate = (row: NotificationTopicDto) => (
        <div className="flex items-center gap-2">
            <span className="font-mono font-semibold text-blue-600">{row.topicCode}</span>
            {row.isSystem && <Lock size={13} className="text-gray-400" />}
        </div>
    );

    const fcmTopicTemplate = (row: NotificationTopicDto) => (
        <span className="font-mono text-xs text-gray-500">{row.fcmTopicName}</span>
    );

    const flagsTemplate = (row: NotificationTopicDto) => (
        <div className="flex flex-wrap gap-1">
            {row.isSystem && <Tag value="System" severity="info" className="text-xs" />}
        </div>
    );

    const statusTemplate = (row: NotificationTopicDto) => (
        <Tag
            value={row.isActive ? "Active" : "Inactive"}
            severity={row.isActive ? "success" : "warning"}
        />
    );

    const actionsTemplate = (row: NotificationTopicDto) => (
        <div className="flex items-center gap-1">
            <button
                type="button"
                onClick={() => openEdit(row)}
                className="p-1.5 rounded-md text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/20 transition-colors"
                title="Edit"
            >
                <Pencil size={15} />
            </button>
            {!row.isSystem && (
                <button
                    type="button"
                    onClick={() => handleDelete(row)}
                    className="p-1.5 rounded-md text-neutral-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:text-danger-400 dark:hover:bg-danger-900/20 transition-colors"
                    title="Delete"
                >
                    <Trash2 size={15} />
                </button>
            )}
        </div>
    );

    const header = (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-3">
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText
                        value={globalFilter}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        placeholder="Search topics..."
                        className="w-64 !pl-10"
                    />
                </span>
                <div className="flex items-center gap-2">
                    <InputSwitch
                        checked={showInactive}
                        onChange={(e) => setShowInactive(e.value ?? false)}
                    />
                    <span className="text-sm text-gray-600">Show inactive</span>
                </div>
            </div>
            <Button
                size="small"
                label="Create Topic"
                icon="pi pi-plus"
                onClick={openCreate}
            />
        </div>
    );

    // Editor dialog footer
    const editorFooter = (
        <div className="flex justify-end gap-2">
            <Button
                size="small"
                label="Cancel"
                icon="pi pi-times"
                outlined
                severity="secondary"
                onClick={() => setEditorVisible(false)}
            />
            <Button
                size="small"
                label={isEditMode ? "Update" : "Create"}
                icon="pi pi-check"
                onClick={handleSave}
                loading={isSaving}
            />
        </div>
    );

    if (isLoading && topics.length === 0) {
        return <Loader />;
    }

    return (
        <>
            <Toast ref={toast} position="bottom-center" />

            <AppCard>
                <AppCard.Header>
                    <div className="flex flex-col gap-1">
                        <span>Notification Topics</span>
                        <small className="text-neutral-500 font-normal">
                            Manage FCM topics for broadcast and targeted push notifications.
                        </small>
                    </div>
                </AppCard.Header>
                <AppCard.Body>
                    <DataTable
                        value={filteredTopics}
                        loading={isLoading}
                        header={header}
                        emptyMessage="No topics found"
                        paginator
                        rows={10}
                        rowsPerPageOptions={[10, 20, 50]}
                        stripedRows
                        pt={{
                            header: { className: "border-gray-200 rounded-t-lg" },
                            wrapper: { className: "border-gray-200 border-x border-b" },
                            bodyRow: { className: "even:!bg-blue-50/50" },
                        }}
                    >
                        <Column
                            field="topicCode"
                            header="Topic Code"
                            body={topicCodeTemplate}
                            sortable
                            style={{ minWidth: "160px" }}
                        />
                        <Column
                            field="displayName"
                            header="Display Name"
                            sortable
                            style={{ minWidth: "180px" }}
                        />
                        <Column
                            field="fcmTopicName"
                            header="FCM Topic"
                            body={fcmTopicTemplate}
                            style={{ minWidth: "220px" }}
                        />
                        <Column
                            header="Flags"
                            body={flagsTemplate}
                            style={{ minWidth: "140px" }}
                        />
                        <Column
                            header="Status"
                            body={statusTemplate}
                            style={{ width: "100px" }}
                        />
                        <Column
                            header="Actions"
                            body={actionsTemplate}
                            style={{ width: "100px" }}
                        />
                    </DataTable>
                </AppCard.Body>
            </AppCard>

            {/* Topic Editor Dialog */}
            <Dialog
                visible={editorVisible}
                onHide={() => setEditorVisible(false)}
                header={isEditMode ? `Edit Topic: ${editingTopic?.topicCode}` : "Create Topic"}
                footer={editorFooter}
                style={{ width: "500px" }}
                modal
                draggable={false}
            >
                <div className="space-y-4">
                    {/* Topic Code */}
                    <div>
                        <label className="block text-sm font-semibold mb-1">
                            Topic Code <span className="text-red-500">*</span>
                        </label>
                        <InputText
                            value={form.topicCode}
                            onChange={(e) =>
                                setForm({ ...form, topicCode: slugify(e.target.value) })
                            }
                            placeholder="e.g., promotions"
                            className={`w-full font-mono ${errors.topicCode ? "p-invalid" : ""}`}
                            disabled={isEditMode}
                            maxLength={100}
                        />
                        {errors.topicCode && (
                            <small className="p-error block mt-1">{errors.topicCode}</small>
                        )}
                        {!isEditMode && form.topicCode && (
                            <small className="text-gray-500 block mt-1">
                                FCM topic: <code className="text-blue-600">{form.topicCode}</code>
                            </small>
                        )}
                        {isEditMode && (
                            <small className="text-gray-500 block mt-1">
                                FCM topic: <code className="text-blue-600">{editingTopic?.fcmTopicName}</code>
                            </small>
                        )}
                    </div>

                    {/* Display Name */}
                    <div>
                        <label className="block text-sm font-semibold mb-1">
                            Display Name <span className="text-red-500">*</span>
                        </label>
                        <InputText
                            value={form.displayName}
                            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                            placeholder="e.g., Promotions & Offers"
                            className={`w-full ${errors.displayName ? "p-invalid" : ""}`}
                            maxLength={200}
                        />
                        {errors.displayName && (
                            <small className="p-error block mt-1">{errors.displayName}</small>
                        )}
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-semibold mb-1">Description</label>
                        <InputTextarea
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder="Optional description of this topic"
                            rows={3}
                            className={`w-full ${errors.description ? "p-invalid" : ""}`}
                            maxLength={500}
                        />
                        {errors.description && (
                            <small className="p-error block mt-1">{errors.description}</small>
                        )}
                    </div>

                    {/* IsActive toggle (not for system topics) */}
                    {!isSystemTopic && isEditMode && (
                        <div className="flex items-center gap-3">
                            <InputSwitch
                                checked={form.isActive}
                                onChange={(e) => setForm({ ...form, isActive: e.value ?? true })}
                            />
                            <div>
                                <span className="text-sm font-semibold">Active</span>
                                <small className="text-gray-500 block">
                                    Inactive topics are hidden from the app
                                </small>
                            </div>
                        </div>
                    )}

                    {/* System topic warning */}
                    {isSystemTopic && (
                        <div className="p-3 bg-blue-50 rounded border border-blue-200">
                            <div className="flex items-center gap-2 text-blue-700 text-sm">
                                <Lock size={14} />
                                <span className="font-semibold">System Topic</span>
                            </div>
                            <small className="text-blue-600 block mt-1">
                                System topics are managed automatically. Only the display name and description can be edited.
                            </small>
                        </div>
                    )}
                </div>
            </Dialog>
        </>
    );
};

export default Topics;
